import { useCallback, useState } from 'react';

/**
 * Wires up "Reset All" for a filter panel whose filter structure is seeded from
 * state that outlives the panel (the FVS context, for instance).
 *
 * The component library does not empty the filters on "Reset All": it restores
 * the values the panel was initialized with. When a panel is seeded from the
 * selections shared across the FVS pages, those seeded values are handed right
 * back to the user instead of being cleared. Remounting the panel makes it
 * rebuild its internal state from the current - by then cleared - filter
 * structure.
 *
 * @param {Function} onClearAll clears the state the filter structure is seeded
 * from. It runs before the panel is remounted.
 * @returns {{filterPanelKey: number, handleClearAll: Function}} the key to put
 * on the filter panel and the handler to pass as its `handleClearAll` prop.
 */
export default function useFilterPanelReset(onClearAll) {
  const [filterPanelKey, setFilterPanelKey] = useState(0);

  const handleClearAll = useCallback(() => {
    if (typeof onClearAll === 'function') {
      onClearAll();
    }
    setFilterPanelKey((key) => key + 1);
  }, [onClearAll]);

  return { filterPanelKey, handleClearAll };
}
